import dayjs from 'dayjs';
import { useContext } from 'react';
import { store } from 'components/Store';
import { compileData } from 'components/Dashboard/Analytics/MentionsByTopicContainer';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { chartDataOptions, createAnalyticsState } from 'components/Dashboard/Analytics/createAnalyticsState';
import { mergeResults } from 'components/Dashboard/Analytics/useCallMentionsByTopic';
import getAppSite from 'utils/getAppSite';
import useSelectedCountriesFilters from 'components/Dashboard/Analytics/useSelectedCountriesFilters';

const useCallPoliticalMentionsByTopic = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { analyticsState } = state ?? {};
  const { generalApiCall } = useGeneralApiCall();

  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';

  const analyticsStateToUse = analyticsState ?? createAnalyticsState();
  const { getSelectedCountriesFilter } = useSelectedCountriesFilters({
    analyticsState: analyticsStateToUse,
    selectedCountriesProps: [
      { name: 'MPs & peers', value: 'UK' },
      { name: 'MSs (Wales)', value: 'Wales' },
      { name: 'MSPs (Scotland)', value: 'Scotland' },
    ],
  });

  const callPoliticalMentionsByTopic = async ({ topic, source, freeSearch, token }) => {
    try {
      let value = {};
      let rawData;
      let parliamentaryRecord = await parliamentaryRecordCall({ topic, source, freeSearch, token });
      if (topic) {
        if (!!parliamentaryRecord) {
          value = {
            Contributions: parliamentaryRecord?.parliamentaryRecords?.series?.find((item) => item.bucket === null)
              ?.datapoints,
          };
          if (showTweets) {
            value = {
              ...value,
              Tweets: parliamentaryRecord?.tweets?.series?.find((item) => item.bucket === null)?.datapoints,
            };
          }
          rawData = value;
          let compiledDataRaw = compileData({ value }, analyticsStateToUse, true).raw;
          let data = {
            labels: compiledDataRaw.map((item) => item.date),
            value,
          };
          return { data, rawData };
        }
      } else if (freeSearch) {
        if (!!parliamentaryRecord) {
          value = {
            'Spoken parliamentary contributions': mergeResults(
              parliamentaryRecord?.series?.find((item) => item.bucket === 'crec')?.datapoints,
              mergeResults(
                parliamentaryRecord?.series?.find((item) => item.bucket === 'parliamentarycontribution')?.datapoints,

                mergeResults(
                  parliamentaryRecord?.series?.find(
                    (item) => item.bucket === 'walescountryspecificparliamentarycontribution'
                  )?.datapoints,
                  parliamentaryRecord?.series?.find(
                    (item) => item.bucket === 'scotlandcountryspecificparliamentarycontribution'
                  )?.datapoints
                )
              )
            ),
            'Written questions': mergeResults(
              parliamentaryRecord?.series?.find((item) => item.bucket === 'questionandanswer')?.datapoints,
              mergeResults(
                parliamentaryRecord?.series?.find((item) => item.bucket === 'walesquestionandanswer')?.datapoints,
                parliamentaryRecord?.series?.find((item) => item.bucket === 'scotlandquestionandanswer')?.datapoints
              )
            ),
            'Committee contributions': parliamentaryRecord?.series?.find(
              (item) => item.bucket === 'seneddcommitteetranscriptcontribution'
            )?.datapoints,
            EDMs: parliamentaryRecord?.series?.find((item) => item.bucket === 'edm')?.datapoints,
          };

          rawData = value;
          let compiledDataRaw = compileData({ value }, analyticsStateToUse, true).raw;
          let data = {
            labels: compiledDataRaw.map((item) => item.date),
            value,
          };
          return { data, rawData };
        }
      }
    } catch (error) {}
  };

  const parliamentaryRecordCall = async ({ topic, source, freeSearch, token }) => {
    try {
      const pathname = `/api/graph-data/${
        freeSearch ? `${token ? '' : 'public-'}date-histogram` : 'mentions-by-keyword-list'
      }`;
      const method = 'post';
      const parliamentaryPositionsFilters =
        chartDataOptions.find((item) => item.name === 'Parliamentary position')?.filters ?? [];
      let filters = [
        {
          field: 'dateTime',
          value: dayjs(dayjs().subtract(5, 'month').startOf('day').format('YYYY-MM-DD'))
            .startOf('month')
            .format('YYYY-MM-DD'),
          operator: 'date_gte',
        },
        {
          field: 'dateTime',
          value: dayjs().add('1', 'day').format('YYYY-MM-DD'),
          operator: 'date_lt',
        },
      ];
      if (getAppSite() !== 'usa') {
        filters = [...filters, ...parliamentaryPositionsFilters, ...getSelectedCountriesFilter()];
      }
      let requestProperties = freeSearch
        ? {
            type: 'ParliamentaryRecord',
            interval: 'monthly',
            filters,
            bucketField: 'contentType',
            order: 'asc',
            query: freeSearch,
          }
        : {
            keywordListId: topic.id,
            from: dayjs(dayjs().subtract(5, 'month').startOf('day').format('YYYY-MM-DD'))
              .startOf('month')
              .format('YYYY-MM-DD'),
            to: dayjs().add('1', 'day').format('YYYY-MM-DD'),
            interval: 'monthly',
            excludeTweets: !showTweets,
          };
      const options = {
        pathname,
        method,
        requestProperties,
        needsAuthentication: true,
        notShowErrorMessage: true,
        requestSource: source,
      };
      if (token) {
        options.requestHeaders = {
          cancelToken: source.token,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      }

      let results = await generalApiCall(options);
      return results;
    } catch (error) {}
  };

  return {
    callPoliticalMentionsByTopic,
  };
};

export default useCallPoliticalMentionsByTopic;
